/**
 * Brand portal.
 *
 * Same auth journey as the creator portal, then organization onboarding.
 * Funded, committed, earned and unallocated stay four distinct amounts
 * because conflating them is how a brand comes to believe paying a creator
 * released budget that was already consumed (architecture 4.9).
 */
import { useCallback, useEffect, useState } from "react";
import {
  PageHeader, StatTile, StateBlock, Money, LinkButton, Eyebrow, Button, Badge,
} from "@ui/primitives";
import { fromMinorString, money } from "@contracts/money";
import { fontSize } from "@design/index";
import { PortalShell, type NavItem } from "@app/shared/PortalShell";
import { useRoute } from "@app/shared/useRoute";
import { ToastHost } from "@app/shared/Toasts";
import { CreatorAvatar, Icons } from "@app/shared/icons";
import { api, getSession, type SessionResponse } from "@app/shared/api";
import {
  SignUpScreen, LoginScreen, VerifyEmailScreen, ForgotPasswordScreen,
  ResetPasswordScreen, VerifyBanner,
} from "@app/shared/AuthScreens";
import { CampaignListScreen, CampaignEditorScreen } from "./Campaigns";
import { BrandSubmissionsScreen } from "./Submissions";
import { AnalyticsScreen } from "./Analytics";
import { OrganizationPanel } from "./Organization";
import { BillingReturnScreen } from "./Funding";
import { BillingHome } from "./Billing";

const NAV: readonly NavItem[] = [
  { label: "Overview", href: "/", icon: Icons.home, tab: true },
  { label: "Organization", href: "/onboarding", icon: Icons.profile,
    shortLabel: "Org", tab: true },
  { label: "Campaigns", href: "/campaigns", icon: Icons.campaigns, tab: true },
  { label: "Submissions", href: "/submissions", icon: Icons.submissions, tab: true },
  { label: "Applicants", href: "/applicants", icon: Icons.work, pending: true },
  { label: "Content review", href: "/review", icon: Icons.shield, pending: true },
  { label: "Analytics", href: "/analytics", icon: Icons.performance, tab: true },
  { label: "Billing", href: "/billing", icon: Icons.payouts, group: "Account" },
  { label: "Team", href: "/team", icon: Icons.connect, pending: true, group: "Account" },
];

function Overview({ session }: { session: SessionResponse }) {
  const [rows, setRows] = useState<Array<{
    currency: string; funded_minor: string; unallocated_minor: string; committed_minor: string;
  }> | null>(null);

  useEffect(() => {
    void api.get<{ campaigns: typeof rows }>("/campaigns").then((r) => {
      if (r.ok) setRows(r.data.campaigns ?? []);
      else setRows([]);
    });
  }, []);

  const ccy = (rows?.[0]?.currency ?? "USD") as "GBP" | "USD" | "EUR";
  const sum = (key: "funded_minor" | "unallocated_minor" | "committed_minor") =>
    fromMinorString(String((rows ?? []).reduce((n, r) => n + Number(r[key] || 0), 0)), ccy);

  return (
    <>
      {!session.user?.emailVerified && session.user
        ? <VerifyBanner email={session.user.email} /> : null}
      <PageHeader eyebrow="Overview" title="Your campaigns"
        description="Brief a campaign, fund it up front, then review the work that comes in. Funding happens before creators ever see the brief."
        actions={<LinkButton href="/onboarding" variant="primary">Organization</LinkButton>} />
      <section aria-label="Budget" style={{ display: "grid", gap: 12 }}>
        <Eyebrow>Budget · {ccy}</Eyebrow>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
          <StatTile label="Funded" value={<Money value={rows ? sum("funded_minor") : money(0, ccy)} emphasis />}
            hint="Received and cleared" tone="ok" />
          <StatTile label="Held for creators" value={<Money value={rows ? sum("committed_minor") : money(0, ccy)} />}
            hint="Reserved when a creator is accepted" tone="process" />
          <StatTile label="Remaining" value={<Money value={rows ? sum("unallocated_minor") : money(0, ccy)} />}
            hint="Funded and still available for new creators" />
        </div>
        <p style={{ fontSize: fontSize.label, color: "var(--faint)" }}>
          Remaining falls when a creator is accepted. Paying an earned amount
          never frees the campaign budget it already consumed. A campaign ends
          when Remaining cannot fund another creator.
        </p>
      </section>
      {rows && rows.length === 0 ? (
        <StateBlock kind="empty" title="No campaigns yet"
          detail="Draft a brief with your rate, cap and deadline. It goes to platform review before it can be funded and activated."
          action={<LinkButton href="/campaigns/new" variant="primary" size="sm">Start a brief</LinkButton>} />
      ) : (
        <StateBlock kind="empty" title="Open a campaign to add funds"
          detail="Inactive campaigns are waiting on funding or activation gates. Money is added per campaign, not to a general wallet."
          action={<LinkButton href="/campaigns" variant="primary" size="sm">Campaigns</LinkButton>} />
      )}
    </>
  );
}

export function App() {
  const [route, navigate] = useRoute();
  const [session, setSession] = useState<SessionResponse | null>(null);

  const refresh = useCallback(async () => {
    const r = await getSession();
    setSession(r.ok ? r.data : { authenticated: false, audience: "brand" });
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const path = route.path;
  const token = route.query.get("token");

  if (path === "/verify-email") {
    return <VerifyEmailScreen token={token} onDone={() => { void refresh(); navigate("/"); }} />;
  }
  if (path === "/reset-password") {
    return <ResetPasswordScreen token={token} onNavigate={navigate} />;
  }

  if (session === null) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center",
                    background: "var(--canvas)", color: "var(--muted)" }}>
        Loading…
      </div>
    );
  }

  if (!session.authenticated) {
    if (path === "/signup") return <SignUpScreen audience="brand" onNavigate={navigate} />;
    if (path === "/forgot-password") return <ForgotPasswordScreen onNavigate={navigate} />;
    return (
      <LoginScreen audience="brand" onNavigate={navigate}
        onSignedIn={() => { void refresh(); navigate("/"); }} />
    );
  }

  async function signOut() {
    await api.post("/auth/logout");
    await refresh();
    navigate("/");
  }

  // A campaign is a real URL: /campaigns for the list, /campaigns/:id for one.
  const campaignId = path.startsWith("/campaigns/") && path !== "/campaigns/new"
    ? path.slice("/campaigns/".length) : null;
  const activeHref = path.startsWith("/submissions") ? "/submissions"
    : path.startsWith("/campaigns") ? "/campaigns"
    : path === "/analytics" ? "/analytics"
    : path === "/onboarding" ? "/onboarding"
    : path.startsWith("/billing") ? "/billing" : "/";

  return (
    <ToastHost>
      <PortalShell
        audience="Brand"
        nav={NAV}
        activeHref={activeHref}
        variant="rail"
        headerEnd={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Button size="sm" onClick={signOut}>Sign out</Button>
            <CreatorAvatar src={session.user?.avatarHref} name={session.user?.displayName ?? undefined} size={36} />
          </span>
        }
      >
        {path === "/analytics" ? (
          <AnalyticsScreen />
        ) : path === "/billing/return" ? (
          <BillingReturnScreen
            campaignId={route.query.get("campaign")}
            quoteId={route.query.get("quote")}
            onOpen={(id) => navigate(`/campaigns/${id}`)} />
        ) : path === "/billing" ? (
          <BillingHome />
        ) : campaignId ? (
          <CampaignEditorScreen
            id={campaignId}
            onBack={() => navigate("/campaigns")}
            tab={route.query.get("tab")}
            onTab={(t) => {
              const q = new URLSearchParams(route.query);
              if (t === "setup") q.delete("tab");
              else q.set("tab", t);
              const qs = q.toString();
              navigate(`/campaigns/${campaignId}${qs ? `?${qs}` : ""}`);
            }}
          />
        ) : path.startsWith("/submissions") ? (
          <BrandSubmissionsScreen
            openId={path === "/submissions" ? null : path.slice("/submissions/".length)}
            onOpen={(id) => navigate(`/submissions/${id}`)}
            onClose={() => navigate("/submissions")} />
        ) : path.startsWith("/campaigns") ? (
          <CampaignListScreen
            startNew={path === "/campaigns/new"}
            onOpen={(id) => navigate(`/campaigns/${id}`)}
            onStartNew={() => navigate("/campaigns/new")}
            onCancelNew={() => navigate("/campaigns")}
          />
        ) : path === "/onboarding" ? (
          <>
            {!session.user?.emailVerified && session.user
              ? <VerifyBanner email={session.user.email} /> : null}
            <PageHeader eyebrow="Organization" title="Your organization"
              description="These details appear on invoices and are checked before a campaign can be funded."
              actions={<Badge tone="neutral">Step 1 of onboarding</Badge>} />
            {session.user?.emailVerified
              ? <OrganizationPanel onSaved={refresh} />
              : <StateBlock kind="forbidden" title="Verify your email first"
                  detail="We need a confirmed address before you can create an organization." />}
          </>
        ) : (
          <Overview session={session} />
        )}
      </PortalShell>
    </ToastHost>
  );
}
